import Query from './Query';
import Store from '../wrappers/Store';
export const modelRegistry = {
    models: {},
    register(name, modelClass) {
        this.models[name] = modelClass;
    },
    getModelClass(name) {
        return this.models[name];
    },
    getModelName(modelClass) {
        for (const [name, model] of Object.entries(this.models)) {
            if (model.entity === modelClass.entity) {
                return name;
            }
        }
        return undefined;
    },
    getSyncableModelClasses() {
        return Object.values(this.models).filter(modelClass => modelClass.syncable);
    }
};
export class Model {
    static DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
    /**
     * The name that is going be used in the store as the key for model data
     */
    static entity;
    /**
     * The primary key to be used for the model.
     */
    static primaryKey = 'id';
    static syncable = false;
    static freeze = false;
    constructor(record) {
    }
    /**
     * Get the model class.
     */
    $self() {
        return this.constructor;
    }
    /**
     * The definition of the fields of the model and its relations.
     */
    static fields() {
        return {};
    }
    /**
     * Create an attr attribute.
     */
    static attr(value, mutator) {
        return {
            type: 'attribute',
            default: value,
            mutator: mutator
        };
    }
    /**
     * Create belongsTo relation.
     */
    static belongsTo(related, foreignKey) {
        return {
            type: 'belongsTo',
            related: related,
            foreignKey: foreignKey
        };
    }
    /**
     * Create hasMany relation.
     */
    static hasMany(related, foreignKey) {
        return {
            type: 'hasMany',
            related: related,
            foreignKey: foreignKey
        };
    }
    /**
     * Create hasOne relation.
     */
    static hasOne(related, foreignKey) {
        return {
            type: 'hasOne',
            related: related,
            foreignKey: foreignKey
        };
    }
    /**
     * Fill the model instance with the given record. If no record were passed,
     * or if the record has any missing fields, each value of the fields will
     * be filled with its default value defined at model fields definition.
     */
    $fill(record = {}) {
        const fields = this.$self().fields();
        for (const key in fields) {
            if (fields[key].type === 'attribute') {
                let value = fields[key].default;
                if (key in record) {
                    value = record[key];
                }
                if (fields[key].mutator) {
                    value = fields[key].mutator?.(value);
                }
                this[key] = value;
            }
        }
    }
    /**
     * Load Relations
     */
    $load(localKeys) {
        const keys = Array.isArray(localKeys) ? localKeys : [localKeys];
        keys.forEach(localKey => {
            const firstKey = localKey.split('.')[0];
            const remainingKeys = localKey.split('.').splice(1);
            const relation = this.$self().relations().find(r => r.localKey === firstKey);
            if (!relation) {
                return;
            }
            if (relation.type === 'belongsTo') {
                const relatedModel = relation.related?.all()?.find((model) => relation.foreignKey ? model.id == this[relation.foreignKey] : false);
                if (remainingKeys.length && relatedModel) {
                    relatedModel.$load(remainingKeys.join('.'));
                }
                this[firstKey] = relatedModel;
            }
            else if (relation.type === 'hasMany') {
                let relatedModels = relation.related?.all()?.filter((model) => relation.foreignKey ? this.id == model[relation.foreignKey] : false);
                if (remainingKeys.length && relatedModels) {
                    relatedModels = relatedModels.map((r) => r.$load(remainingKeys.join('.')));
                }
                this[firstKey] = relatedModels;
            }
            else if (relation.type === 'hasOne') {
                let relatedModel = relation.related?.all()?.find((model) => relation.foreignKey ? this.id == model[relation.foreignKey] : false);
                if (remainingKeys.length && relatedModel) {
                    relatedModel = relatedModel.$load(remainingKeys.join('.'));
                }
                this[firstKey] = relatedModel;
            }
        });
        return this;
    }
    /**
     * Load All Relations
     */
    $loadAll() {
        const relations = this.$self().relations();
        return this.$load(relations.map(r => r.localKey));
    }
    /**
     * Get Model Relations
     */
    static relations() {
        const relations = [];
        const fields = this.fields();
        for (const key in fields) {
            if (fields[key].type !== 'attribute') {
                relations.push({ ...fields[key], localKey: key });
            }
        }
        return relations;
    }
    /**
     * Get all records.
     */
    static all() {
        return Store.models().all(this.entity);
    }
    /**
     * Find a record.
     */
    static find(id) {
        return Store.models().find(this.entity, this.primaryKey, id);
    }
    /**
     * Get last record.
     */
    static last() {
        const allModels = Store.models().all(this.entity);
        return allModels[allModels.length - 1];
    }
    /**
     * Get query instance.
     */
    static query() {
        return new Query(this);
    }
    /**
     * Create records initially.
     */
    static init(sendToStore) {
        const data = this.freeze ? Object.freeze(sendToStore.data) : sendToStore.data;
        const PayloadType = { model: this, data, init: true };
        Store.models().create(PayloadType);
    }
    /**
     * Create records.
     */
    static create(sendToStore) {
        const data = this.freeze ? Object.freeze(sendToStore.data) : sendToStore.data;
        const PayloadType = { model: this, data };
        Store.models().create(PayloadType);
    }
    /**
     * Insert records.
     */
    static insert(sendToStore) {
        const data = this.freeze ? Object.freeze(sendToStore.data) : sendToStore.data;
        const PayloadType = { model: this, data };
        Store.models().insert(PayloadType);
    }
    /**
     * Delete all records.
     */
    static deleteAll() {
        this.create({ data: [] });
    }
    /**
     * Update records.
     */
    static update(data) {
        let dataArray = Array.isArray(data) ? data : [data];
        let dataArrayFrozen = this.freeze ? Object.freeze(dataArray) : dataArray;
        const PayloadType = { model: this, data: dataArrayFrozen };
        Store.models().update(PayloadType);
    }
    /**
     * Save record.
     */
    $save() {
        const data = { model: this.$self(), data: [this.$toJson()] };
        Store.models().insert(data);
    }
    /**
     * Delete record.
     */
    $delete() {
        const data = { model: this.$self(), data: this.$toJson() };
        Store.models().delete(data);
    }
    /**
     * Serialize field values into json.
     */
    $toJson(includeRelations = true) {
        let record = {};
        for (const key in this) {
            const field = this.$self().fields()[key];
            if (!field)
                continue;
            if (field.type === 'attribute') {
                record[key] = this[key];
            }
            else if (includeRelations && this[key]) {
                record[key] = this[key];
            }
        }
        return record;
    }
    /**
     * Serialize field values for the sync queue (SyncData). Defaults to
     * $toJson(); models whose sync payload must differ from their in-store
     * representation override this (see Order).
     */
    $toSyncJson() {
        return this.$toJson();
    }
    static getModelClass(className) {
        return modelRegistry.getModelClass(className);
    }
    static getModelName(modelClass) {
        return modelRegistry.getModelName(modelClass);
    }
    static async syncTerminals(data) {
        const modelClass = this.getModelClass('AppEvent');
        const model = this.getModelName(this);
        if (!model)
            return;
        data = JSON.parse(JSON.stringify(data));
        modelClass?.build('syncTerminals', { model, ...data }).send();
    }
    async markForSync(action) {
        const SyncDataClass = this.$self().getModelClass('SyncData');
        const model = this.$self().getModelName(this.$self());
        if (!model || !SyncDataClass)
            return;
        const syncData = await SyncDataClass.createNew(model, [this.$toSyncJson()], action);
        await syncData.saveForSync();
        return syncData;
    }
    async removeFromSync() {
        const SyncDataClass = this.$self().getModelClass('SyncData');
        const model = this.$self().getModelName(this.$self());
        if (!model || !SyncDataClass)
            return;
        await SyncDataClass.deleteModels([this.$toJson()]);
    }
    static round(value, decimals) {
        const factor = Math.pow(10, decimals);
        return Math.round(+value * factor + Number.EPSILON) / factor;
    }
    round(value, decimals) {
        return this.$self().round(value, decimals);
    }
}
//# sourceMappingURL=Model.js.map